import * as Sentry from "@sentry/react";

/**
 * Sprint 9b — Sentry error tracking.
 *
 * Off by default (no DSN = no network calls). To activate in prod,
 * set VITE_SENTRY_DSN in Vercel env. All errors are then captured
 * with full source-map symbolication.
 *
 * PII policy:
 *   - We tag breadcrumbs with { org_id, user_id } for correlation
 *   - We NEVER send guest names, document numbers, emails — the
 *     beforeSend hook scrubs anything that looks like PII from the
 *     event payload before it leaves the browser.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN ?? "";
const ENV = import.meta.env.VITE_SENTRY_ENV ?? (import.meta.env.DEV ? "development" : "production");
const RELEASE = (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown");

declare const __APP_VERSION__: string;

export function initSentry(): void {
  if (!DSN) {
    if (import.meta.env.DEV) console.info("[sentry] no DSN — error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: `diyafa@${RELEASE}`,
    // Session replay is off for privacy — turn on later if needed.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    // 10% perf trace sample in prod to keep quota low.
    tracesSampleRate: ENV === "production" ? 0.1 : 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // PII scrubber: strip guest/customer names, doc numbers, emails,
    // phones from any event before it ships off the browser.
    beforeSend(event) {
      try {
        scrubPii(event as unknown as Record<string, unknown>);
      } catch (e) {
        console.warn("[sentry] scrubPii threw", e);
      }
      return event;
    },
  });
}

/** Call after login so every subsequent event is tagged with org + user. */
export function identifySentryUser(userId: string, orgId: string, role: string): void {
  if (!DSN) return;
  Sentry.setUser({ id: userId });
  Sentry.setTags({ org_id: orgId, role });
}

export function clearSentryUser(): void {
  if (!DSN) return;
  Sentry.setUser(null);
}

/** Recursively walk an object and redact string values that look like
 *  PII. Keys that commonly carry PII are overwritten with "[redacted]";
 *  everything else passes through. Keeps the event useful for debugging
 *  while keeping customer data out of our error tracker. */
const PII_KEYS = new Set([
  "full_name_en", "full_name_ar", "guest_name_en", "guest_name_ar",
  "customer_name_en", "customer_name_ar",
  "email", "phone", "address",
  "document_number", "doc_number", "driver_license",
  "national_id", "passport_number",
]);

function scrubPii(node: unknown): void {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node)) {
    node.forEach(scrubPii);
    return;
  }
  for (const key of Object.keys(node as Record<string, unknown>)) {
    const value = (node as Record<string, unknown>)[key];
    if (PII_KEYS.has(key) && typeof value === "string") {
      (node as Record<string, unknown>)[key] = "[redacted]";
    } else if (typeof value === "object") {
      scrubPii(value);
    }
  }
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export { Sentry };
